import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/feature/PageHeader';
import Pagination from '@/components/base/Pagination';
import { newsCategories, newsList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 4;

export default function News() {
  usePageMeta(
    '影视资讯 - 红桃影视',
    '红桃影视资讯频道，提供最新影视行业动态、热片盘点、幕后专访与专题解读，及时掌握影视圈大小事。',
  );

  const [category, setCategory] = useState('全部');
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)), []);
  const filtered = useMemo(
    () => (category === '全部' ? sorted : sorted.filter((n) => n.category === category)),
    [sorted, category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const items = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="pb-6">
      <PageHeader
        title="影视资讯"
        description="聚焦影视行业最新动态、热片口碑与幕后故事，为你提供有价值的影视阅读内容。（本站资讯均为示范数据）"
        items={[{ label: '影视资讯' }]}
      />

      <div className="px-4 md:px-6 lg:px-10 mt-6">
        <div className="flex flex-wrap items-center gap-2">
          {newsCategories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setCategory(c);
                setPage(1);
              }}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                category === c
                  ? 'bg-primary-500 text-background-50 font-medium'
                  : 'bg-background-200 text-foreground-700 hover:bg-background-300'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {items.map((article) => (
            <Link
              key={article.id}
              to={`/news/${article.id}`}
              className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-100 border border-background-300/60 hover:border-primary-500/50 transition-colors"
            >
              <span className="w-full sm:w-52 shrink-0 aspect-[16/9] rounded-md overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} 资讯`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </span>
              <span className="min-w-0 flex-1 flex flex-col">
                <span className="flex items-center gap-3">
                  <span className="px-2.5 py-0.5 rounded-full bg-accent-500/20 text-accent-400 text-xs font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-500">{article.date}</span>
                </span>
                <span className="mt-2 text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors line-clamp-2">
                  {article.title}
                </span>
                <span className="mt-2 text-sm text-foreground-600 line-clamp-2">
                  {article.summary}
                </span>
                <span className="mt-auto pt-3 text-xs text-foreground-500 flex items-center gap-3">
                  <span>
                    <i className="ri-user-line mr-1" />
                    {article.author}
                  </span>
                  <span className="text-primary-400">
                    阅读全文
                    <i className="ri-arrow-right-s-line ml-0.5" />
                  </span>
                </span>
              </span>
            </Link>
          ))}
        </div>

        <Pagination current={currentPage} total={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}